import { Box, Card, CardContent, LinearProgress, Stack, Typography } from "@mui/material";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useRef } from "react";

import type { ListPublicPackagesResponseItem, PublicPackageResponse } from "../../publicPackages/publicPackagesApi";
import { usePublicPackages } from "../../publicPackages/usePublicPackages";
import { CardMediaImg } from "../CardImage";
import { CardLinkArea } from "../CardLinkArea";

const TILE_WIDTH = 188;
const TILE_GAP = 12;

function useSubordinatePackages(props: { forPackage: PublicPackageResponse }) {
  const { packages, isLoading } = usePublicPackages();

  const matchingPackages = packages.filter((p) => p.parentPackageId === props.forPackage.id.id);

  return {
    isLoading,
    packages: matchingPackages,
  };
}

function SubordinatePackagesView(props: { packages: ListPublicPackagesResponseItem[] }) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    updateScrollState();

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);

    el.addEventListener("scroll", updateScrollState, {
      passive: true,
    });

    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", updateScrollState);
    };
  }, [updateScrollState, props.packages.length]);

  if (props.packages.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 1, minWidth: 0, width: "100%", overflow: "hidden" }}>
      <Stack
        direction="row"
        sx={{
          mb: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Opcje pakietu
        </Typography>
      </Stack>

      <Box
        ref={scrollerRef}
        sx={{
          display: "flex",
          gap: `${TILE_GAP}px`,
          overflowX: "auto",
          overflowY: "hidden",
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          pb: 1,
          px: 0.5,
          mx: -0.5,
          scrollBehavior: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          cursor: "grab",
          "&:active": {
            cursor: "grabbing",
          },
        }}
      >
        {props.packages.map((p) => (
          <Card
            key={p.id.id}
            elevation={0}
            sx={{
              flex: `0 0 ${TILE_WIDTH}px`,
              width: TILE_WIDTH,
              minWidth: 0,
              borderRadius: 1,
              border: "1px solid rgba(0,0,0,0.08)",
              overflow: "hidden",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
              },
            }}
          >
            <CardLinkArea
              to={`../${p.id.id}`}
              sx={{
                display: "block",
                height: "100%",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              <Box sx={{ position: "relative" }}>
                <CardMediaImg
                  component="img"
                  height="100"
                  imageUrl={p.images[0]?.thumbnail?.url}
                  alt={p.images[0]?.name ?? p.name}
                  sx={{
                    display: "block",
                    width: "100%",
                    objectFit: "cover",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 65%)",
                  }}
                />
              </Box>

              <CardContent
                sx={{
                  p: 1.25,
                  "&:last-child": {
                    pb: 1.25,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.25,
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    overflowWrap: "anywhere",
                  }}
                >
                  {p.id.userFriendlyId} • {p.name}
                </Typography>
              </CardContent>
            </CardLinkArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

export function SubordinatePackages(props: { forPackage: PublicPackageResponse }) {
  const { packages, isLoading } = useSubordinatePackages(props);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (isEmpty(packages)) {
    return null;
  }

  return <SubordinatePackagesView packages={packages} />;
}
